/**
 * useProjectBudget
 *
 * Fetches EBProjects budget data for a given Scheduler project ID.
 * Used by ResourceRequestPlanDrawer and ResourceRequestsTab to surface budget
 * context during allocation.
 *
 * A project's budget changes outside the scheduler — amending its dates or its
 * role budgets in EBProjects respreads the budgeted hours across other months —
 * so the cached figures are only ever a first paint: every read revalidates
 * against EBProjects and replaces what the cache holds.
 */

import { useState, useEffect, useRef } from 'react';
import { projectsApi, EBProjectsBudgetComposite, EBProjectsProject } from '@/services/api';
import { monthKeyOf, isMonthInWindow, type MonthWindow } from '@/lib/demandMonthWindow';

/**
 * The months a project runs for, as 'YYYY-MM' keys, either end inclusive. An
 * end is absent when the project leaves that date unset, and is then unbounded.
 */
export type ProjectMonthWindow = MonthWindow;
interface UseProjectBudgetResult {
  budgetData: EBProjectsBudgetComposite | null;
  budgetLoading: boolean;
  budgetError: string | null;
  roleBudgets: EBProjectsBudgetComposite['roleBudgets'];
  /** Unbounded until the project's dates are known. */
  projectMonthWindow: ProjectMonthWindow;
}

const budgetCache = new Map<string, EBProjectsBudgetComposite>();
const windowCache = new Map<string, ProjectMonthWindow>();

const UNBOUNDED_WINDOW: ProjectMonthWindow = {};

// The requests tab and the plan drawer both read the active request's project,
// so opening a plan asked EBProjects for the same project and budget twice.
// Concurrent readers share one request each; a later read still revalidates,
// which is what this hook exists for.
const projectInFlight = new Map<string, Promise<EBProjectsProject>>();
const budgetInFlight = new Map<string, Promise<EBProjectsBudgetComposite>>();

function share<T>(
  inFlight: Map<string, Promise<T>>,
  key: string,
  start: () => Promise<T>,
): Promise<T> {
  const pending = inFlight.get(key);
  if (pending) return pending;
  const request = start().finally(() => {
    if (inFlight.get(key) === request) inFlight.delete(key);
  });
  inFlight.set(key, request);
  return request;
}

/** True when a 'YYYY-MM' month falls inside a project's window. */
export function isMonthInProjectWindow(
  month: string,
  window: ProjectMonthWindow,
): boolean {
  return isMonthInWindow(month, window);
}

export function useProjectBudget(
  projectId: string | number | null | undefined,
): UseProjectBudgetResult {
  const [budgetData, setBudgetData] = useState<EBProjectsBudgetComposite | null>(null);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [budgetError, setBudgetError] = useState<string | null>(null);
  const [projectMonthWindow, setProjectMonthWindow] =
    useState<ProjectMonthWindow>(UNBOUNDED_WINDOW);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;

    if (!projectId) {
      setBudgetData(null);
      setBudgetLoading(false);
      setBudgetError(null);
      setProjectMonthWindow(UNBOUNDED_WINDOW);
      return;
    }

    const key = String(projectId);

    // The project's own dates: its budget keeps the months it was originally
    // spread over even after the dates are amended, so the months the budget
    // carries are not the months the project runs for.
    setProjectMonthWindow(windowCache.get(key) ?? UNBOUNDED_WINDOW);
    share(projectInFlight, key, () => projectsApi.getFromEBProjectsById(key))
      .then((project) => {
        if (cancelledRef.current) return;
        const next: ProjectMonthWindow = {
          start: monthKeyOf(project.startDate),
          end: monthKeyOf(project.endDate),
        };
        windowCache.set(key, next);
        setProjectMonthWindow(next);
      })
      .catch(() => {
        // An unknown window is unbounded, which shows every month the budget
        // carries — how the panel read before it knew the project's dates.
        if (cancelledRef.current) return;
        windowCache.delete(key);
        setProjectMonthWindow(UNBOUNDED_WINDOW);
      });

    // What was last fetched for the project shows straight away, so reopening a
    // request paints its budget without a spinner — while the fetch below
    // decides whether those figures still hold.
    const cached = budgetCache.get(key);
    setBudgetData(cached ?? null);
    setBudgetLoading(!cached);
    setBudgetError(null);

    share(budgetInFlight, key, () => projectsApi.getBudgetsFromEBProjects(key))
      .then((res) => {
        if (cancelledRef.current) return;
        budgetCache.set(key, res);
        setBudgetData(res);
        setBudgetError(null);
      })
      .catch((err) => {
        if (cancelledRef.current) return;
        // Stale figures are worse than none: they are the amended project's old
        // budget, which is what this hook exists to stop showing.
        budgetCache.delete(key);
        setBudgetData(null);
        setBudgetError(err instanceof Error ? err.message : 'Failed to fetch budget');
      })
      .finally(() => {
        if (!cancelledRef.current) setBudgetLoading(false);
      });

    return () => {
      cancelledRef.current = true;
    };
  }, [projectId]);

  return {
    budgetData,
    budgetLoading,
    budgetError,
    roleBudgets: budgetData?.roleBudgets ?? [],
    projectMonthWindow,
  };
}

/** Strip case, punctuation and spacing so "Senior Engineer" matches "senior-engineer". */
function normaliseRoleName(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Find the role budget entry for a request's role.
 *
 * The PSA role identifier is authoritative — role names are free text in
 * EBProjects and often differ from the request's copy by case, punctuation or
 * spacing, which previously reported the role as having no budget. Match on
 * `roleId` first, then on the normalised name.
 *
 * The same role can be budgeted under several cost centres, so a request that
 * carries one is matched only within that centre's lines — matching on role
 * alone would resolve every such request to whichever line came first. A
 * request without a cost centre still matches across all of them.
 */
export function findRoleBudget(
  roleBudgets: EBProjectsBudgetComposite['roleBudgets'],
  roleName: string | undefined,
  roleCode?: number | null,
  costCentreCode?: string | null,
) {
  const candidates = costCentreCode
    ? roleBudgets.filter((rb) => rb.costCentreCode === costCentreCode)
    : roleBudgets;
  if (!candidates.length) return undefined;

  if (roleCode != null) {
    const byId = candidates.find((rb) => rb.roleId != null && rb.roleId === roleCode);
    if (byId) return byId;
  }

  if (!roleName) return undefined;
  const normalised = normaliseRoleName(roleName);
  if (!normalised) return undefined;
  return candidates.find(
    (rb) => rb.roleName != null && normaliseRoleName(rb.roleName) === normalised,
  );
}

/**
 * Safely parse the monthlyHours JSON string from a role budget entry.
 * Returns an empty record on parse failure.
 */
export function parseMonthlyHours(
  monthlyHoursJson: string | undefined,
): Record<string, number> {
  if (!monthlyHoursJson) return {};
  try {
    return JSON.parse(monthlyHoursJson) as Record<string, number>;
  } catch {
    return {};
  }
}

/**
 * Returns the EDS-compatible accent colour for a given budget usage percentage.
 * < 70% → teal (on track), 70-90% → warning (at risk), > 90% → destructive (over budget)
 */
export function budgetHealthAccent(percentUsed: number): 'teal' | 'warning' | 'destructive' {
  if (percentUsed < 70) return 'teal';
  if (percentUsed <= 90) return 'warning';
  return 'destructive';
}
